import {Mic, MicOff, Phone, X} from 'lucide-react'
import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {getTheme} from '../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {useVoiceAnimationState} from '../hooks/useVoiceAnimationState'
import {TranscriptLine, VoiceCallError, VoiceCallStatus} from '../hooks/useVoiceCallState'
import PromptModal from '../modal/PromptModal'
import AiAgentCircle from '../ui/AiAgentCircle'
import VoiceTranscriptionPanel from './VoiceTranscriptionPanel'

type VoiceModePageProps = {
  status: VoiceCallStatus
  transcriptionEnabled: boolean | null
  isMuted: boolean
  error?: VoiceCallError | null
  transcript?: TranscriptLine[]
  interimUserTranscript?: string
  micLevel?: number
  onEndCall: () => void
  onMuteToggle: () => void
  onReturnToChat: () => void
  onMinimize?: () => void
  onRetry?: () => void
  awaitingResponse?: boolean
}

const VoiceModePage: React.FC<VoiceModePageProps> = ({
  status,
  transcriptionEnabled,
  isMuted,
  error,
  transcript = [],
  interimUserTranscript = '',
  micLevel = 0,
  onEndCall,
  onMuteToggle,
  onReturnToChat,
  onRetry,
  awaitingResponse = false,
}) => {
  const intl = useIntl()
  const [callDuration, setCallDuration] = useState(0)
  const [isSplitView, setIsSplitView] = useState(false)
  const [showEndCallPrompt, setShowEndCallPrompt] = useState(false)

  const animationState = useVoiceAnimationState({
    status,
    isAiSpeaking: status === 'responding',
    isAwaitingResponse: awaitingResponse,
    isMuted,
    micLevel,
  })
  const resolvedTheme = useMemo(() => getTheme(), [])
  const isDarkMode = resolvedTheme === 'dark'

  useEffect(() => {
    if (
      status === 'listening' ||
      status === 'responding' ||
      status === 'muted' ||
      status === 'walkthrough_paused' ||
      awaitingResponse
    ) {
      const interval = setInterval(() => {
        setCallDuration((prev) => prev + 1)
      }, 1000)
      return () => clearInterval(interval)
    } else if (status === 'idle' || status === 'ended' || status === 'choosing_transcription') {
      setCallDuration(0)
    }
  }, [status, awaitingResponse])

  useEffect(() => {
    if (transcriptionEnabled === true && transcript.length > 0 && !isSplitView) {
      setIsSplitView(true)
    } else if (transcriptionEnabled !== true && isSplitView) {
      setIsSplitView(false)
    }
  }, [transcript, isSplitView, transcriptionEnabled])

  const title = intl.formatMessage({id: 'CHAT.VOICE.TITLE'})
  const connectingLabel = intl.formatMessage({
    id: 'CHAT.VOICE.CONNECTING',
  })
  const listeningLabel = intl.formatMessage({
    id: 'CHAT.VOICE.LISTENING',
  })
  const speakingLabel = intl.formatMessage({
    id: 'CHAT.VOICE.SPEAKING',
  })
  const endLabel = intl.formatMessage({id: 'CHAT.VOICE.END_CALL'})
  const returnLabel = intl.formatMessage({
    id: 'CHAT.VOICE.RETURN_TO_CHAT',
  })
  const muteLabel = intl.formatMessage({id: 'CHAT.VOICE.MUTE'})
  const unmuteLabel = intl.formatMessage({id: 'CHAT.VOICE.UNMUTE'})
  const errorLabel = intl.formatMessage({id: 'CHAT.VOICE.ERROR'})
  const retryLabel = intl.formatMessage({id: 'CHAT.VOICE.RETRY'})
  const continueCallLabel = intl.formatMessage({id: 'CHAT.VOICE.CONTINUE_CALL'})
  const endCallConfirmLabel = intl.formatMessage({id: 'CHAT.VOICE.END_CALL_CONFIRM'})
  const thinkingLabel = intl.formatMessage({id: 'CHAT.VOICE.THINKING'})

  const handleEndCallClick = () => {
    setShowEndCallPrompt(true)
  }

  const handleConfirmEndCall = () => {
    setShowEndCallPrompt(false)
    onEndCall()
    onReturnToChat()
  }

  const handleCancelEndCall = () => {
    setShowEndCallPrompt(false)
  }

  const renderStateBadge = () => {
    if (status === 'error') return errorLabel
    if (awaitingResponse) return thinkingLabel
    if (status === 'connecting') return connectingLabel
    if (status === 'listening') return listeningLabel
    if (status === 'responding') return speakingLabel
    if (status === 'muted') return muteLabel
    if (status === 'walkthrough_paused')
      return intl.formatMessage({id: 'CHAT.VOICE.WALKTHROUGH_ACTIVE'}) || 'Walkthrough Active'
    if (status === 'ended') return endLabel
    return title
  }

  const showTranscription = transcriptionEnabled === true

  const error_message = useMemo(() => {
    if (!error) return null

    const errorKeyMap: Record<string, string> = {
      permission_denied: 'CHAT.VOICE.ERROR_PERMISSION_DENIED',
      permission_revoked: 'CHAT.VOICE.ERROR_PERMISSION_REVOKED',
      speech_recognition_not_supported: 'CHAT.VOICE.ERROR_NOT_SUPPORTED',
      microphone_not_accessible: 'CHAT.VOICE.ERROR_NO_MICROPHONE',
      speech_recognition_error: 'CHAT.VOICE.ERROR_RECOGNITION_FAILED',
      unknown: 'CHAT.VOICE.ERROR_UNKNOWN',
    }

    const key = errorKeyMap[error.type] || 'CHAT.VOICE.ERROR_UNKNOWN'
    return intl.formatMessage({id: key})
  }, [error, intl])

  return (
    <div
      className={`voice-mode-overlay ${isDarkMode ? 'dark-mode' : ''}`}
      role='dialog'
      aria-modal='true'
      aria-label={title}
    >
      {status === 'error' ? (
        <div className={`voice-call-container ${isDarkMode ? 'dark-mode' : ''}`}>
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '24px',
              maxWidth: '400px',
            }}
          >
            <div style={{fontSize: '20px', fontWeight: 700, color: '#ef4444'}}>{errorLabel}</div>
            <div
              style={{
                fontSize: '14px',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                lineHeight: '1.6',
              }}
            >
              {error_message}
            </div>
            <div style={{display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '8px'}}>
              {error?.retryable && onRetry && (
                <button onClick={onRetry} className='btn btn-primary'>
                  {retryLabel}
                </button>
              )}
              <button onClick={onReturnToChat} className='btn btn-light'>
                {returnLabel}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`voice-call-container ${isSplitView ? 'split-view' : 'centered'} ${isDarkMode ? 'dark-mode' : ''}`}
          >
            <button className='close-button' onClick={onReturnToChat} aria-label='Close'>
              <X size={20} />
            </button>

            <div className='voice-left-panel'>
              <div className='timer-display'>
                {`${String(Math.floor(callDuration / 60)).padStart(2, '0')}:${String(callDuration % 60).padStart(2, '0')}`}
              </div>

              <div className='status-text'>{renderStateBadge()}</div>

              <AiAgentCircle
                isListening={animationState.isListening}
                isAiSpeaking={animationState.isSpeaking}
                isAiThinking={animationState.isThinking}
                isMuted={isMuted}
                volumeLevel={micLevel}
                isEntering={animationState.isEntering}
                isExiting={animationState.isExiting}
                compact={false}
                label={intl.formatMessage({id: 'CHAT.VOICE.AI_AGENT'})}
              />

              <div className='control-buttons'>
                <button
                  className={`control-button ${isMuted ? 'muted' : 'mute'}`}
                  onClick={onMuteToggle}
                  disabled={status === 'connecting'}
                  aria-label={isMuted ? unmuteLabel : muteLabel}
                >
                  {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
                </button>
                <button
                  className='control-button end-call'
                  onClick={handleEndCallClick}
                  disabled={status === 'connecting'}
                  aria-label={endLabel}
                >
                  <Phone size={24} />
                </button>
              </div>
            </div>

            {showTranscription && (
              <div className='voice-right-panel'>
                <div
                  className='voice-transcription-container'
                  style={{
                    background: 'transparent',
                    boxShadow: 'none',
                    padding: 0,
                  }}
                >
                  <div
                    className='voice-transcription-header'
                    style={{
                      background: 'transparent',
                      boxShadow: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    <h4 style={{margin: 0}}>Conversation</h4>
                    <span className='message-count'>{transcript.length} messages</span>
                  </div>
                  <VoiceTranscriptionPanel
                    transcript={transcript}
                    interimUserTranscript={interimUserTranscript}
                    status={status}
                  />
                </div>
              </div>
            )}
          </div>

          <PromptModal
            open={showEndCallPrompt}
            onClose={handleCancelEndCall}
            title={endLabel}
            body={endCallConfirmLabel}
            primaryLabel={endLabel}
            onPrimary={handleConfirmEndCall}
            secondaryLabel={continueCallLabel}
            onSecondary={handleCancelEndCall}
            placement='center'
            width={400}
            theme={resolvedTheme}
          />
        </>
      )}
    </div>
  )
}

export default VoiceModePage
